import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_WORKLOG_RULES,
  addWireDays,
  formatHours,
  taskSchema,
  toWireDate,
  wireMonday,
  overtimeTypeSchema,
  worklogOverviewSchema,
  type OvertimeType,
  worklogRulesSchema,
  worklogSchema,
  type Worklog,
  type WorklogOverview,
  type WorklogRules,
} from '@forecastify/shared';
import { Toaster, type Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useTimeSectionHeader } from './time-section-header';
import { useLiveInvalidation } from '../../live/use-live';
import { TIME_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList, usePermissionSet } from '../permissions-hook';
import { useFirstLoad } from '../use-first-load';
import { TimeScreen, type LogDraft, type TaskChoice } from './time-screen';
import { useMyWeek } from '../dashboard/use-my-week';
import { useWorkspaceNavigate } from '../../workspace-path';
import { worklogBody, worklogHours } from './worklog-form';
import { billsNowhere } from './bills-nowhere';
import {
  dismissRecent,
  readDismissed,
  suggestionKey,
  type DismissedRecents,
} from './dismissed-recents';

/**
 * The Időrögzítés overview, with its data.
 *
 * ⚠️ **Two requests, not five.** The aggregates come from `/worklogs/overview` — computed in SQL,
 * because summing `numeric` in a browser reintroduces floating-point drift on the figures somebody
 * bills from. The entries come from one list call over a window wide enough to serve both the
 * selected day and the recents, so selecting another day inside the week costs no request at all.
 */

/**
 * ⚠️ **Recents look back 30 days, and the mockup looks back forever.** Its `logRecent` walks the
 * whole in-memory array; ours would be an unbounded query on a table that grows by an entry per
 * person per task per day. Thirty days is stated here rather than hidden: somebody who has not
 * touched a task in a month re-picks it from the search box, which is one keystroke.
 */
const RECENT_DAYS = 30;
const RECENT_COUNT = 6;

/**
 * ⚠️⚠️ **TWO READS RATHER THAN ONE, AND THE CURSOR IS WHY.**
 *
 * C11 fetched one 37-day window and sliced the selected day out of it in the browser. That was
 * exact only because the endpoint returned everything; with `/worklogs` paged at 50 by default the
 * same request silently becomes *"the fifty newest of the last five weeks"*, and a day early in the
 * window loses its entries with nothing on screen to say so — the worst kind of wrong, because the
 * page still looks finished.
 *
 * So each read is now bounded by what it is for:
 *
 *  * the **day** list asks for one day. `DAY_LIMIT` is far above any real day's entries, and if
 *    somebody ever exceeds it the header's total comes from the aggregate and visibly disagrees —
 *    a discrepancy beats a silent truncation;
 *  * the **recents** strip asks for the newest `RECENT_LIMIT` of the window and needs six distinct
 *    task-and-note pairs out of them. Paging further would only find pairs nobody is about to
 *    re-log.
 */
const DAY_LIMIT = 200;
const RECENT_LIMIT = 50;

let toastCounter = 0;
const nextToastId = (): string => `time-${String((toastCounter += 1))}`;

/**
 * One `details` entry, by its code.
 *
 * ⚠️ **`details` is a LIST and the arms need specific members of it**, so a message cannot just
 * take `details[0]`: `day_over_maximum` carries two entries and reading the wrong one puts the
 * maximum where the total belongs. Rule 5 says clients branch on `code`; this is the same rule one
 * level down.
 */
function detailFor(
  envelope: { error?: { details?: { code?: string; message?: string }[] } } | null,
  code: string,
): string {
  return envelope?.error?.details?.find((entry) => entry.code === code)?.message ?? '';
}

export function TimeRoute() {
  /* The mockup's header dropdown for this section — see `time-section-header.tsx`. */
  useTimeSectionHeader('overview');
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();

  /**
   * C13's rules, so the screen can say why a day cannot be written to.
   *
   * ⚠️ **Defaulted rather than withheld.** A failed read must not make the screen refuse every day
   * — the server is the one enforcing, and `DEFAULT_WORKLOG_RULES` is what it falls back to for a
   * workspace with no row, so the two agree on the same fallback.
   */
  const [rules, setRules] = useState<WorklogRules>(DEFAULT_WORKLOG_RULES);

  /* Today, as a wire date. The strip's week is derived from whatever day is selected. */
  const [day, setDay] = useState(() => toWireDate(new Date()));
  /**
   * 16 Oct 2026 · *Heti* or *Havi*. ⚠️ Component state, like the day — the route's own note says
   * why the address carries neither.
   */
  const [span, setSpan] = useState<'week' | 'month'>('week');
  const go = useWorkspaceNavigate();
  /* 16 Oct 2026 · *A hét összesítése* — the selected day's week of allocations. */
  const { week } = useMyWeek(day);
  const [overview, setOverview] = useState<WorklogOverview | null>(null);
  /** The selected day's entries — one day, asked for as one day. */
  const [entries, setEntries] = useState<readonly Worklog[]>([]);
  /** ⚠️ Item 58 · suggestions somebody has put away. `dismissed-recents.ts` carries the shape. */
  const [dismissed, setDismissed] = useState<DismissedRecents>(() =>
    readDismissed(session.workspaceId),
  );

  /** The newest entries of the surrounding window; the recents strip is derived from these. */
  const [recentPool, setRecentPool] = useState<readonly Worklog[]>([]);
  const [results, setResults] = useState<readonly TaskChoice[]>([]);
  /* ⚠️ Read once, outside `reload`: the types do not change when somebody picks another day, and
     re-reading them on every arrow press would be three requests where one is needed. */
  const [overtimeTypes, setOvertimeTypes] = useState<readonly OvertimeType[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const monday = useMemo(() => wireMonday(day) ?? day, [day]);
  const window = useMemo(() => {
    const from = addWireDays(monday, -RECENT_DAYS) ?? monday;
    const to = addWireDays(monday, 6) ?? monday;
    return { from, to };
  }, [monday]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THIS SCREEN IS *MY* TIME, AND IT WAS NOT ASKING FOR MINE.        │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * All three reads went out with no `memberId`, and the server reads that as *everybody* for
     * anybody holding `worklog.read` at workspace scope — an owner, an admin, a project manager. So
     * Időrögzítés showed the whole workspace's hours: the day panel listed other people's entries,
     * *A hét összesítése* was the workspace's week, and the strip's totals were sixteen-hour days.
     * Somebody with `own` scope saw their own and never met it, which is why it stood.
     *
     * ⚠️ **It also greyed out the whole strip.** A verdict answers *did THIS PERSON work their
     * day*, so `overview` skips the day kernel when the subject is everybody — correctly. With no
     * subject there was nothing to judge, and every pill drew the neutral bar.
     *
     * ⚠️ **The member id is per-WORKSPACE.** The same account is a different member in each, so it
     * comes off the active workspace rather than from the account.
     */
    const me = session.workspaces.find((entry) => entry.id === session.workspaceId)?.memberId;
    const mine = me === undefined ? '' : `&memberId=${me}`;

    const [aggregate, dayList, recentList] = await Promise.all([
      session
        .request(`/worklogs/overview?day=${day}${mine}${span === 'month' ? '&month=1' : ''}`)
        .catch(() => null),
      session
        .request(`/worklogs?from=${day}&to=${day}&limit=${String(DAY_LIMIT)}${mine}`)
        .catch(() => null),
      session
        .request(
          `/worklogs?from=${window.from}&to=${window.to}&limit=${String(RECENT_LIMIT)}${mine}`,
        )
        .catch(() => null),
    ]);

    if (aggregate === null || dayList === null || recentList === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!aggregate.ok) {
      setFailure(await failureOf(aggregate));
      return;
    }
    if (!dayList.ok) {
      setFailure(await failureOf(dayList));
      return;
    }

    const parsedOverview = worklogOverviewSchema.safeParse(await aggregate.json());
    const parsedDay = parseList(worklogSchema, await dayList.json());
    if (!parsedOverview.success || parsedDay === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    setFailure(null);
    setOverview(parsedOverview.data);
    setEntries(parsedDay);

    /* ⚠️ The recents read is deliberately NOT fatal. It feeds a convenience strip; failing the
     * whole screen over it would take the day's entries down with it, which is what somebody
     * actually came for. */
    if (recentList.ok) {
      const parsedRecents = parseList(worklogSchema, await recentList.json());
      if (parsedRecents !== null) setRecentPool(parsedRecents);
    }
  }, [session, day, window, span]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /**
   * ⚠️ **The overtime types, for the quick-log's picker.** `listTypes` is gated on `worklog.read`
   * rather than `settings.view` precisely so this control can offer them — the service says so.
   * Not fatal: without them the picker is absent and an hour is still an hour.
   */
  useEffect(() => {
    void (async () => {
      const response = await session.request('/overtime/types').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(overtimeTypeSchema, await response.json());
      if (parsed !== null) setOvertimeTypes(parsed.filter((type) => type.active));
    })();
  }, [session]);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. A colleague's change
   * refetches this in place — no spinner, no blank, no reload. R-19. */
  useLiveInvalidation(TIME_TOPICS, reload);

  /**
   * The quick-log's task picker.
   *
   * ⚠️ **An EMPTY query means "my open tasks", not "every task".** The mockup does the same — its
   * `logTaskResults` falls back to `x.as === ME` — because the list is eight rows and a workspace
   * has thousands. The server has no "assigned to me" default, so it is asked for one explicitly.
   */
  const searchTasks = useCallback(
    async (query: string): Promise<void> => {
      const trimmed = query.trim();
      const params = new URLSearchParams();
      if (trimmed === '') {
        /* ⚠️ The member id is per-WORKSPACE — `session.workspaces` carries one each, because the
         * same account is a different member in each. There is no single "my member id". */
        const me = session.workspaces.find((entry) => entry.id === session.workspaceId);
        if (me !== undefined) params.set('assigneeMemberId', me.memberId);
      } else {
        params.set('search', trimmed);
      }

      const response = await session.request(`/tasks?${params.toString()}`).catch(() => null);
      if (!response?.ok) {
        setResults([]);
        return;
      }
      const parsed = parseList(taskSchema, await response.json());
      if (parsed === null) {
        setResults([]);
        return;
      }

      setResults(
        parsed.slice(0, 8).map((task) => ({
          id: task.id,
          key: task.key,
          title: task.title,
          projectName: task.projectName,
          projectColor: task.projectColor,
        })),
      );
    },
    [session],
  );

  /**
   * The rules, once per mount.
   *
   * ⚠️ **Not fatal, and not part of any guard.** A caller whose read fails should get their week
   * with the defaults applied, not an error page — the server enforces either way.
   */
  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/worklogs/rules').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = worklogRulesSchema.safeParse(await response.json().catch(() => null));
      if (parsed.success) setRules(parsed.data);
    })();
  }, [session]);

  /* Load the default result set once, so the dropdown is not empty on first focus. */
  const searched = useRef(false);
  useEffect(() => {
    if (searched.current) return;
    searched.current = true;
    void searchTasks('');
  }, [searchTasks]);

  const toast = (entry: Omit<Toast, 'id'>): void => {
    setToasts((current) => [...current, { ...entry, id: nextToastId() }]);
  };

  /**
   * ⚠️⚠️ **THE REFUSAL WORDING, IN ONE PLACE, BECAUSE TWO WRITERS NEED IT.** Every branch here
   * used to live inside `write`; `writeJson` below wants the created row and would otherwise
   * either duplicate the mapping or word the same refusal differently. One sentence per code —
   * rule 5 — whichever helper a call site reached for.
   */
  const report = async (response: Response | null): Promise<void> => {
    if (response === null) {
      setServerError(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      const envelope = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { code?: string; message?: string }[] };
      } | null;
      const code = envelope?.error?.code ?? 'unavailable';

      /**
       * Branching on `code`, never on `message` — rule 5.
       *
       * ⚠️ **P-1's two refusals get two sentences, which is the whole reason they are two codes.**
       * *"Ask to be added to the project"* and *"you were not on this project on that day"* are
       * different instructions; one message for both leaves the second unactionable.
       */
      if (code === 'not_a_project_member') setServerError(t('time.error.notAMember'));
      /* 26 Oct 2026 · the person has *Időt rögzít* off — the server refuses their hours. */
      else if (code === 'member_not_tracking') {
        setServerError(
          t('time.error.notLogging', { name: envelope?.error?.details?.[0]?.message ?? '' }),
        );
      }
      /* U-7 · the task sits on a licence line item, which takes no hours. */
      else if (code === 'line_item_takes_no_work') {
        setServerError(t('error.line_item_takes_no_work'));
      }
      /* 30 Oct 2026 · the task's contract is closed, which takes no new hour. */
      else if (code === 'contract_closed') {
        setServerError(
          t('error.contract_closed', { name: envelope?.error?.details?.[0]?.message ?? '' }),
        );
      } else if (code === 'outside_membership_window')
        setServerError(t('time.error.outsideWindow'));
      else if (code === 'not_permitted' || code === 'permission_denied') {
        setServerError(t('time.error.notPermitted'));
      } else if (code === 'day_over_maximum') {
        /**
         * C13. ⚠️ **The figures come from `details`, not from anything computed here.** The server
         * decided the day was full; recomputing the total client-side to word the message would give
         * two numbers that can disagree, and the one on screen would be the wrong one.
         */
        setServerError(
          t('time.error.dayFull', {
            total: detailFor(envelope, 'day_total'),
            max: detailFor(envelope, 'day_maximum'),
          }),
        );
      } else if (code === 'outside_backdate_window') {
        setServerError(t('time.error.tooFarBack', { earliest: detailFor(envelope, 'earliest') }));
      } else if (code === 'month_closed') {
        setServerError(t('time.error.monthClosed', { day: detailFor(envelope, 'closed_month') }));
        /* ⚠️ An approved claim freezes its hour — `worklog.service.ts` argues it at the error. */
      } else if (code === 'overtime_approved') {
        setServerError(t('time.error.overtimeApproved'));
      } else if (code === 'validation_failed') setServerError(t('time.error.hours'));
      else if (code === 'conflict') setServerError(t('error.conflict'));
      else if (code === 'not_found') setServerError(t('error.not_found'));
      else setServerError(t('error.unavailable'));
      return;
    }
  };

  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (!response?.ok) {
      await report(response);
      return false;
    }
    await reload();
    return true;
  };

  /**
   * The same write, returning the created row's id.
   *
   * ⚠️ **A second helper rather than widening `write`.** Twelve call sites want the boolean and
   * exactly one wants the body; changing the shared one would make every one of them read a
   * value they ignore.
   */
  const writeJson = async (
    path: string,
    init: RequestInit,
  ): Promise<{ readonly id: string; readonly billsNowhere: boolean } | null> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (!response?.ok) {
      await report(response);
      return null;
    }
    const body = (await response.json().catch(() => null)) as { id?: string } | null;
    await reload();
    return typeof body?.id === 'string' ? { id: body.id, billsNowhere: billsNowhere(body) } : null;
  };

  /* ⚠️ No client-side day filter any more: the request asked for ONE day, so every row is that
   * day's. Filtering here as well would mask a server-side mistake instead of surfacing it. */

  /**
   * ⚠️ **Deduplicated by task AND note, which is what makes the list useful.** Six rows of the same
   * task with the same note is one row of information; the pair is what a person re-logs. The
   * mockup keys its `seen` map on exactly `task + '|' + note`.
   */
  const recents = useMemo(() => {
    const seen = new Set<string>();
    const out: Worklog[] = [];
    for (const entry of recentPool) {
      const key = suggestionKey(entry.taskId, entry.note);
      if (seen.has(key)) continue;
      seen.add(key);

      /**
       * ⚠️⚠️ **DISMISSED ONLY WHILE IT IS THE SAME WORKLOG — item 58's *"until used again"*.**
       * The stored value is the id that was on screen when somebody put this suggestion away, so
       * logging the same task with the same note again produces a NEWER worklog whose id differs
       * and the row returns. No expiry, no timestamps, nothing to be wrong about clocks.
       *
       * ⚠️ **`continue` BEFORE the push but AFTER `seen`**, so a dismissed pair does not let the
       * next-oldest worklog of the same pair take its place — which would put the identical
       * suggestion straight back.
       */
      if (dismissed[key] === entry.id) continue;

      out.push(entry);
      if (out.length === RECENT_COUNT) break;
    }
    return out;
  }, [recentPool, dismissed]);

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        {overview !== null && (
          <TimeScreen
            loading={loading}
            day={day}
            overview={overview}
            /* ⚠️ Item 58 · one suggestion, put away until it is logged again. */
            onDismissRecent={(entry) => {
              setDismissed((current) =>
                dismissRecent(
                  session.workspaceId,
                  current,
                  suggestionKey(entry.taskId, entry.note),
                  entry.id,
                ),
              );
            }}
            entries={entries}
            recents={recents}
            results={results}
            canLog={permissions.has('worklog.create')}
            canEdit={permissions.has('worklog.update')}
            rules={rules}
            /* ⚠️ `worklog.lock` lifts the BACKDATING window and not the monthly lock — the server
             * checks the lock before it consults the permission. */
            mayOverride={permissions.has('worklog.lock')}
            serverError={serverError}
            onSelectDay={setDay}
            span={span}
            onSpan={setSpan}
            /**
             * ⚠️ A week steps seven days and keeps the weekday; a month steps through the
             * calendar and keeps the day of the month where the month has one — the 31st of
             * March steps back to the 28th of February, not into March 3rd.
             */
            onShift={(periods) => {
              if (span === 'week') {
                setDay(addWireDays(day, periods * 7) ?? day);
                return;
              }
              const [year, month, date] = day.split('-').map(Number);
              const last = new Date(Number(year), Number(month) - 1 + periods + 1, 0).getDate();
              setDay(
                toWireDate(
                  new Date(Number(year), Number(month) - 1 + periods, Math.min(Number(date), last)),
                ),
              );
            }}
            onToday={() => {
              setDay(toWireDate(new Date()));
            }}
            /* ⚠️ The period's first day — unless today is inside it, which is the day somebody
               jumping back to the current week or month means. The mockup's `tlPickWeek`. */
            onPickPeriod={(start) => {
              const today = toWireDate(new Date());
              const end =
                span === 'week'
                  ? (addWireDays(start, 6) ?? start)
                  : toWireDate(new Date(Number(start.slice(0, 4)), Number(start.slice(5, 7)), 0));
              setDay(today >= start && today <= end ? today : start);
            }}
            week={week}
            onOpenLineItem={(contract, lineItem) => {
              go(`/contracts/${contract}/${lineItem}`);
            }}
            onOpenTask={(taskKey) => {
              go(`/tasks/${taskKey}`);
            }}
            onSearchTasks={(query) => void searchTasks(query)}
            /* ⚠️ The COUNT, not the list — `0147` moved the choice of kind to the approver, so
               all the form needs to know is whether a claim can be raised at all. */
            overtimeOffered={overtimeTypes.length > 0}
            onLog={(draft: LogDraft) => {
              void (async () => {
                const hours = worklogHours(draft);
                if (hours === null) return;
                const created = await writeJson('/worklogs', {
                  method: 'POST',
                  /* ⚠️ The shared body since item 20 — it carries `startedAt`, which this screen
                     never used to ask for. `worklog-form.tsx` says when it goes as `null`. */
                  body: JSON.stringify({ taskId: draft.taskId, ...worklogBody(draft, hours) }),
                });
                if (created === null) return;

                /**
                 * ⚠️⚠️ **TWO WRITES, AND THE ORDER IS THE POINT.** A claim points at a worklog, so
                 * the hour has to exist first — there is no endpoint that creates both. The hour is
                 * therefore saved even if the claim is refused, which is the right way round: an
                 * hour that was worked is a fact, and the claim about it is a request. The toast
                 * says which of the two happened.
                 */
                if (draft.overtime) {
                  const claimed = await write('/overtime', {
                    method: 'POST',
                    /* ⚠️⚠️ **THE WORKLOG AND NOTHING ELSE — `0147`.** It used to send the type
                       this person chose, which let them price their own hours. */
                    body: JSON.stringify({ worklogId: created.id }),
                  });
                  /* ⚠️ The hour landed either way — only the claim can have been refused, and the
                     error strip already says why. The toast reports what actually happened rather
                     than claiming a success the second write did not have. */
                  toast({
                    title: claimed ? t('time.toast.loggedOvertime') : t('time.toast.logged'),
                    variant: claimed && !created.billsNowhere ? 'success' : 'info',
                    ...(created.billsNowhere ? { description: t('worklog.unidentified') } : {}),
                  });
                  return;
                }
                /* ⚠️ OS-1 · saved, and billed to no line item — said, never refused. */
                toast(
                  created.billsNowhere
                    ? {
                        title: t('time.toast.logged'),
                        description: t('worklog.unidentified'),
                        variant: 'info',
                      }
                    : { title: t('time.toast.logged'), variant: 'success' },
                );
              })();
            }}
            onSetHours={(entry, hours) => {
              void (async () => {
                const previous = entry.hours;
                const ok = await write(`/worklogs/${entry.id}`, {
                  method: 'PATCH',
                  body: JSON.stringify({ hours, version: entry.version }),
                });
                if (!ok) return;

                /**
                 * ⚠️ **Undo re-sends the OLD value, and it re-reads the version rather than reusing
                 * the one in hand.** The write it undoes bumped the version, so `entry.version`
                 * is now stale and a naive undo would answer `409` — a conflict against a change
                 * the same person just made.
                 */
                toast({
                  title: t('time.toast.changed'),
                  description: `${entry.taskKey} · ${formatHours(previous)} → ${formatHours(hours)}`,
                  variant: 'success',
                  action: {
                    label: t('time.undo'),
                    failureLabel: t('time.undo.failed'),
                    /**
                     * ⚠️⚠️ **THE PROMISE IS RETURNED, and `Toaster` requires it.** `state-rules.md`
                     * §7: undo is a reverse mutation and can itself fail. A `void` here makes a
                     * failed undo indistinguishable from a successful one.
                     */
                    onAction: async () => {
                      const fresh = await session
                        .request(`/worklogs?taskId=${entry.taskId}&from=${day}&to=${day}`)
                        .catch(() => null);
                      if (!fresh?.ok) throw new Error('reload failed');
                      const parsed = parseList(worklogSchema, await fresh.json());
                      const current = parsed?.find((row) => row.id === entry.id);
                      if (current === undefined) throw new Error('the entry is gone');
                      const ok = await write(`/worklogs/${entry.id}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ hours: previous, version: current.version }),
                      });
                      if (!ok) throw new Error('the undo was refused');
                    },
                  },
                });
              })();
            }}
            onDelete={(entry) => {
              void (async () => {
                const ok = await write(`/worklogs/${entry.id}`, { method: 'DELETE' });
                if (ok) {
                  toast({
                    title: t('time.toast.deleted'),
                    description: `${entry.taskKey} · ${formatHours(entry.hours)}`,
                    variant: 'success',
                  });
                }
              })();
            }}
          />
        )}
      </ScreenState>

      <Toaster
        toasts={[...toasts]}
        label={t('time.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </>
  );
}
